import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { TicketBoard as Board, TicketKarte, TicketSpalte, TicketZeile, Unternehmen } from "@kundeninfo/api-client";
import { useTelefon } from "@kundeninfo/ui";
import { Chip, Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { TicketZeitleiste } from "./TicketZeitleiste.js";
import type { AblageZiel } from "./TicketZeitleiste.js";
import { TicketKarteAnsicht } from "./TicketKarte.js";
import { SpaltenDialog } from "./SpaltenDialog.js";
import { TicketRaster } from "./TicketRaster.js";
import { TicketFilterleiste, filterAnfrage } from "./TicketFilterleiste.js";
import { heuteBeschriftung, popupFuerTag, tagBeschriftung, zeileFuer } from "./ticketFormat.js";
import { useKartenZiehen, zielAttribute as ablageAttribute } from "./ziehhilfe.js";
import { TicketQuittung } from "./TicketQuittung.js";
import { TicketVerschieben } from "./TicketVerschieben.js";
import type { TicketsClient } from "./ticketsClient.js";
import { gemerkteAnsicht, leseFilter, leseOffen, merkeAnsicht, merkeFilter, merkeOffen } from "./boardSpeicher.js";
import type { BoardFilter } from "./boardSpeicher.js";
import "./Tickets.css";

/**
 * DAS BOARD (Spec „Board"): Spalten nebeneinander (Übergang T-1), je Spalte die Zeitleiste,
 * Werkzeugleiste mit Unternehmen (115, Ort nach 127 vorläufig), Kunde, Zuständig, „nur
 * verknüpfte", Darstellung je Spalte/Raster (F19) und „+ Aufgabe". Lädt beim Öffnen, bei
 * Fokus-Rückkehr, alle 60 s und nach Mitternacht neu (F20); das Rollfeld bleibt dabei dasselbe
 * Element, damit die Scrollposition stehen bleibt (Hausregel). Filter, Darstellung und Auf-/
 * Zuklappen merkt sich der Browser (F19, `boardSpeicher.ts`). Übergang T-1: Spaltenliste mit
 * Kopf, Zähler und Menü als eigener Code (`.tb__*`), bis me-design ein Board-Element hat.
 * Ziehen am Griff (Übergang T-2, `ziehhilfe.ts`) und „Verschieben …" (F22) gehen an die Ablage;
 * die Karte steht sofort am Ziel, die Antwort des Servers gleicht ab, 409 lädt neu (Review-
 * Fokus 2). Danach die Quittung mit „Rückgängig" als Gegenbuchung (Übergang T-4, F23).
 * Die Filter teilt das Board mit der Liste (`TicketFilterleiste.tsx`, Ruling A4); das Raster
 * (`TicketRaster.tsx`, 118) zeichnet im selben Rollfeld mit denselben Karten und Ablagezielen.
 */

export { ANSICHT_SCHLUESSEL } from "./boardSpeicher.js";
export type { BoardFilter } from "./boardSpeicher.js";

export { filterAnfrage } from "./TicketFilterleiste.js";

const BERLIN = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", hour: "2-digit",
  minute: "2-digit", second: "2-digit", hourCycle: "h23" });

/** Wanduhr-Rechnung: An Tagen der Zeitumstellung liegt der Timer eine Stunde daneben (Gesamtreview
 *  M11). Feuert er zu früh, bleibt `heute` gleich und der Effekt stellt sich nicht neu — der
 *  60-s-Takt lädt dann binnen einer Minute nach Mitternacht nach, die Zeitleiste rückt trotzdem. */
export function msBisMitternachtBerlin(jetzt: Date): number {
  const t = BERLIN.formatToParts(jetzt);
  const w = (typ: string) => Number(t.find((x) => x.type === typ)?.value ?? "0");
  return ((24 - w("hour")) * 3600 - w("minute") * 60 - w("second")) * 1000 - jetzt.getMilliseconds();
}

export function quittungsText(t: TicketKarte, spaltenName: string): string {
  return t.resubmitDate === null ? `${t.ticketNumber} → ${spaltenName}`
    : `${t.ticketNumber} → ${spaltenName}, Wiedervorlage ${tagBeschriftung(t.resubmitDate)}`;
}

/** Die Zeile, auf die ein Ziel zeigt (−1: keine Zeile, etwa ein Datum jenseits der 30 Tage). */
function zielZeile(z: AblageZiel, zeilen: readonly TicketZeile[]): number {
  if (z.ziel === "JETZT") return 0;
  const zeile = z.ziel === "TAG" ? zeilen.find((x) => x.art === "TAG" && x.datum === z.datum)
    : zeilen.find((x) => x.art === z.ziel && x.von === z.datum);
  return zeile?.index ?? -1;
}

/** Die gesetzten Filter als kurze Texte für die eingeklappte Filterzeile am Telefon. */
function aktiveFilter(f: BoardFilter, p: Pick<TicketBoardProps, "companies" | "ich">,
                      tickets: readonly TicketKarte[]): string[] {
  const texte: string[] = [];
  if (f.company !== "ALLE") texte.push(p.companies.find((c) => c.id === f.company)?.shortCode ?? "Unternehmen");
  if (f.customer !== "") {
    texte.push(tickets.flatMap((t) => t.customers).find((c) => c.id === f.customer)?.name ?? "gewählter Kunde");
  }
  if (f.assignee !== "") {
    texte.push(f.assignee === "none" ? "Niemand" : f.assignee === p.ich.id ? "Ich"
      : tickets.find((t) => t.assignee?.id === f.assignee)?.assignee?.name ?? "gewählte Person");
  }
  if (f.linked) texte.push("nur verknüpfte");
  return texte;
}

type Quittung = { text: string; ticketId: string; version: number;
                  vorher: { columnId: string; resubmitDate: string | null } };

const GEAENDERT = "Das Ticket wurde inzwischen geändert.";

export type TicketBoardProps = {
  client: TicketsClient; companies: readonly Unternehmen[]; ich: { id: string; name: string };
  onOeffnen: (id: string) => void; onAnlegen: (companyId: string | null) => void; neuLaden?: number;
};

export function TicketBoard(p: TicketBoardProps): ReactElement {
  const [board, setBoard] = useState<Board | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [filter, setFilter] = useState<BoardFilter>(() => leseFilter(p.companies.map((c) => c.id)));
  const [ansicht, setAnsicht] = useState<"spalten" | "raster">(gemerkteAnsicht);
  const telefon = useTelefon();
  const [telefonSpalte, setTelefonSpalte] = useState<string | null>(null);
  // F36: am Telefon kein Raster; die gemerkte Ansicht (`ansicht`) bleibt dabei unberührt.
  const wirksameAnsicht = telefon ? "spalten" : ansicht;
  // Fehlt die gewählte Spalte (gelöscht, neue Id), gilt die Eingangsspalte, sonst die erste.
  const aktiveTelefonSpalte = board === null ? "" : (board.spalten.find((s) => s.id === telefonSpalte)
    ?? board.spalten.find((s) => s.isInbox) ?? board.spalten[0])?.id ?? "";
  const [offen, setOffen] = useState<Record<string, boolean>>(leseOffen);
  const [spaltenDialog, setSpaltenDialog] = useState<{ spalte: TicketSpalte | null } | null>(null);
  const rollfeld = useRef<HTMLDivElement | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [verschieben, setVerschieben] = useState<TicketKarte | null>(null);
  const [quittung, setQuittung] = useState<Quittung | null>(null);
  // Am Telefon klappen die Filter ein (Sichtprobe 5): eine Zeile „Filter“ mit den aktiven Werten.
  const [filterOffen, setFilterOffen] = useState(false);
  // Während eines Zugs lädt das Board nicht im Takt oder bei Fokus neu (wie `useZiehOrdnung`
  // es seinen Aufrufern anbietet): Die Zielzone soll unter dem Zeiger nicht wechseln.
  const ziehtRef = useRef(false);

  // Nur die jüngste Anfrage zeichnet: Ein schneller Filterwechsel darf nicht von der Antwort
  // auf den alten Filter überholt werden.
  const lauf = useRef(0);
  const laden = useCallback(async () => {
    const nummer = ++lauf.current;
    try {
      const neu = await p.client.getTicketBoard(filterAnfrage(filter));
      if (nummer !== lauf.current) return;
      setBoard(neu);
      setFehler(null);
    } catch {
      if (nummer === lauf.current) setFehler("Das Board konnte nicht geladen werden.");
    }
  }, [p.client, filter]);

  useEffect(() => { void laden(); }, [laden, p.neuLaden]);
  const ladenRef = useRef(laden);
  ladenRef.current = laden;

  useEffect(() => {
    const takt = setInterval(() => { if (!ziehtRef.current) void laden(); }, 60_000);
    const fokus = () => { if (!ziehtRef.current) void laden(); };
    window.addEventListener("focus", fokus);
    return () => { clearInterval(takt); window.removeEventListener("focus", fokus); };
  }, [laden]);

  useEffect(() => {
    if (board === null) return undefined;
    const t = setTimeout(() => { void laden(); }, msBisMitternachtBerlin(new Date()) + 1000);
    return () => clearTimeout(t);
  }, [board?.heute, laden]);

  const filterSetzen = (f: Partial<BoardFilter>) => {
    const neu = { ...filter, ...f };
    setFilter(neu);
    merkeFilter(neu);
  };
  const ansichtSetzen = (a: "spalten" | "raster") => {
    setAnsicht(a);
    merkeAnsicht(a);
  };
  const offenSetzen = (id: string, wert: boolean) => {
    const neu = { ...offen, [id]: wert };
    setOffen(neu);
    merkeOffen(neu);
  };
  async function ablegen(ticketId: string, ziel: AblageZiel): Promise<void> {
    const t = board?.tickets.find((x) => x.id === ticketId);
    if (board === null || t === undefined) return;
    // Am eigenen Platz abgelegt (eigene Spalte und Kopf, eigene Zeile oder derselbe Tag): keine
    // Anfrage, keine Quittung — auch nicht für Jetzt auf einem überfälligen oder undatierten Ticket.
    if (ziel.columnId === t.columnId && (ziel.ziel === "KOPF"
        || (ziel.ziel === "TAG" && ziel.datum === t.resubmitDate)
        || zielZeile(ziel, board.zeilen) === zeileFuer(t.resubmitDate, board.zeilen, board.heute))) return;
    const vorher = { columnId: t.columnId, resubmitDate: t.resubmitDate };
    // Vorschau bis zur Antwort: Tag und Jetzt zeichnet die Karte sofort am Ziel; das verbindliche
    // Datum (Berliner Tag, Werktag bei Monat/später) setzt der Server (F13, F18).
    setBoard({ ...board, tickets: board.tickets.map((x) => (x.id !== ticketId ? x : {
      ...x, columnId: ziel.columnId,
      ...(ziel.ziel === "TAG" ? { resubmitDate: ziel.datum } : ziel.ziel === "JETZT" ? { resubmitDate: board.heute } : {}),
    })) });
    // Ein Neuladen, das vor dem Ende der Ablage startete (Takt, Fokus), brächte den alten Stand
    // samt alter Version zurück (Gesamtreview M3): Der Zähler verwirft es zu Beginn und am Ende;
    // lief eins dazwischen, lädt das Board danach frisch.
    const stand = ++lauf.current;
    try {
      const neu = await p.client.ablageTicket(ticketId,
        { columnId: ziel.columnId, ziel: ziel.ziel, datum: ziel.datum, version: t.version });
      const dazwischen = lauf.current !== stand;
      lauf.current += 1;
      if (dazwischen) void ladenRef.current();
      setBoard((b) => (b === null ? b : { ...b, tickets: b.tickets.map((x) => (x.id === ticketId ? neu : x)) }));
      const spalte = board.spalten.find((s) => s.id === neu.columnId)?.name ?? "";
      setQuittung({ text: quittungsText(neu, spalte), ticketId, version: neu.version, vorher });
      setHinweis(null);
    } catch (e) {
      await laden();
      setHinweis(e instanceof ApiError && e.status === 409 ? GEAENDERT
        : e instanceof ApiError ? (e.fields?.[0]?.message ?? e.message) : "Das Ticket ließ sich nicht verschieben.");
    }
  }

  /** Rückgängig (F23): Gegenbuchung per PATCH mit der NEUEN Version auf Spalte und Datum von vorher. */
  async function rueckgaengig(q: Quittung): Promise<void> {
    setQuittung(null);
    try {
      await p.client.updateTicket(q.ticketId, q.version, q.vorher);
      setHinweis(null);
    } catch (e) {
      setHinweis(e instanceof ApiError && e.status === 409 ? GEAENDERT : "Rückgängig ist nicht gelungen.");
    }
    await laden();
  }

  const zieh = useKartenZiehen({ rollfeld, onAblage: (id, z) => { void ablegen(id, z); } });
  ziehtRef.current = zieh.zug !== null;

  const karte = (t: TicketKarte) => (
    <TicketKarteAnsicht key={t.id} t={t} heute={board!.heute} ichId={p.ich.id} companies={p.companies}
      onOeffnen={p.onOeffnen} onVerschieben={setVerschieben}
      griff={zieh.griff(t.id)} zieht={zieh.zug?.ticketId === t.id} />
  );
  // Ein Tag am Wochenende oder Feiertag bleibt Ziel, wird aber als Warnung getönt (119).
  const zielAttribute = (z: AblageZiel): Record<string, string> =>
    ablageAttribute(z, z.ziel === "TAG" && z.datum !== null ? popupFuerTag(z.datum, board?.kalender ?? []) : "");

  const offeneAnzahl = (board?.tickets ?? []).filter((t) => t.stateKind !== "DONE").length;

  return (
    <section className="tb" aria-label="Tickets">
      <div className="tb__kopf">
        <h2 className="tb__pfad">Tickets › Board</h2>
        <span className="tb__offen">{offeneAnzahl} offen</span>
        {board !== null && <span className="tb__heute">Heute {heuteBeschriftung(board.heute)}</span>}
      </div>
      <div className="tb__werkzeuge" role="toolbar" aria-label="Werkzeuge des Boards">
        {telefon && (
          <div className="tb__filterzeile">
            <KiKnopf art="leise" ausgeklappt={filterOffen} onClick={() => setFilterOffen(!filterOffen)}>
              Filter
            </KiKnopf>
            <span className="tb__aktivefilter">
              {aktiveFilter(filter, p, board?.tickets ?? []).map((text) => <Chip key={text}>{text}</Chip>)}
            </span>
            <KiKnopf art="primaer" onClick={() => p.onAnlegen(filter.company === "ALLE" ? null : filter.company)}>
              + Aufgabe
            </KiKnopf>
          </div>
        )}
        {/* Eingeklappt bleiben die Filter im Baum (versteckt): Der gewählte Kunde behält seinen Namen. */}
        <div className="tb__filter" hidden={telefon && !filterOffen}>
          <TicketFilterleiste client={p.client} companies={p.companies} ich={p.ich} tickets={board?.tickets ?? []}
            filter={filter} onFilter={filterSetzen} />
        </div>
        {!telefon && (
          <Optionen beschriftung="Darstellung" darstellung="segmente" richtung="waagerecht" wert={ansicht}
            onGeaendert={(d) => ansichtSetzen(d.wert === "raster" ? "raster" : "spalten")}>
            <option value="spalten">je Spalte</option>
            <option value="raster">Raster</option>
          </Optionen>
        )}
        {!telefon && (
          <KiKnopf art="primaer" onClick={() => p.onAnlegen(filter.company === "ALLE" ? null : filter.company)}>
            + Aufgabe
          </KiKnopf>
        )}
      </div>
      {/* Die Quittung steht fest unten links, im Baum aber gleich nach der Werkzeugleiste: Der
          Tabweg erreicht „Rückgängig" vor allen Karten. */}
      {quittung !== null && (
        <TicketQuittung text={quittung.text} onSchliessen={() => setQuittung(null)}
          onRueckgaengig={() => { void rueckgaengig(quittung); }} />
      )}
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {hinweis !== null && <Hinweis art="warnung">{hinweis}</Hinweis>}
      {telefon && board !== null && (
        <Optionen beschriftung="Spalte" darstellung="segmente" richtung="waagerecht"
          wert={aktiveTelefonSpalte} onGeaendert={(d) => setTelefonSpalte(d.wert ?? null)}>
          {board.spalten.map((s) => (
            <option key={s.id} value={s.id}>
              {`${s.name} (${board.tickets.filter((t) => t.columnId === s.id).length})`}
            </option>
          ))}
        </Optionen>
      )}
      <div className="tb__spalten" ref={rollfeld} data-ansicht={wirksameAnsicht}>
        {board !== null && wirksameAnsicht === "raster" && (
          <TicketRaster board={board} karte={karte} zielAttribute={zielAttribute} />
        )}
        {board !== null && wirksameAnsicht === "spalten" && board.spalten
          .filter((s) => !telefon || s.id === aktiveTelefonSpalte)
          .map((s) => {
          const tickets = board.tickets.filter((t) => t.columnId === s.id);
          const ueberfaellig = tickets.filter((t) => t.resubmitDate !== null && t.resubmitDate < board.heute
                                                   && s.timelineMode !== "NONE").length;
          const istOffen = offen[s.id] ?? s.timelineMode === "EXPANDED";
          return (
            <section key={s.id} className="tb__spalte" role="region" aria-label={s.name}>
              <header className="tb__spaltenkopf" {...zielAttribute({ columnId: s.id, ziel: "KOPF", datum: null, text: s.name })}>
                <h3 className="tb__spaltenname">{s.name}</h3>
                <span className="tb__anzahl" aria-label={`${tickets.length} Tickets`}>{tickets.length}</span>
                {ueberfaellig > 0 && (
                  <span className="tb__ueberfaellig" aria-label={`${ueberfaellig} überfällig`}>{ueberfaellig}</span>
                )}
                {s.timelineMode !== "NONE" && (
                  <KiKnopf art="leise" name={`Zeitleiste ${s.name} ${istOffen ? "zuklappen" : "aufklappen"}`}
                    onClick={() => offenSetzen(s.id, !istOffen)}>
                    {istOffen ? "zuklappen" : "aufklappen"}
                  </KiKnopf>
                )}
                <KiKnopf art="leise" name={`Spalte ${s.name} bearbeiten`}
                  onClick={() => setSpaltenDialog({ spalte: s })}>⋯</KiKnopf>
              </header>
              <TicketZeitleiste spalte={s} zeilen={board.zeilen} heute={board.heute} tickets={tickets}
                kalender={board.kalender} offen={istOffen} karte={karte} zielAttribute={zielAttribute} />
            </section>
          );
        })}
        <div className="tb__neu">
          <KiKnopf art="leise" onClick={() => setSpaltenDialog({ spalte: null })}>Neue Spalte</KiKnopf>
        </div>
      </div>
      {zieh.zug !== null && (
        // Nur fürs Auge (der Griff ist für die Vorlesestimme stumm); angesagt wird das Ergebnis
        // einmal, in der Quittung.
        <div className="tb__vorschau" aria-hidden="true">{zieh.zug.ziel?.text ?? "Ziel wählen …"}</div>
      )}
      {verschieben !== null && board !== null && (
        <TicketVerschieben t={verschieben} spalten={board.spalten} zeilen={board.zeilen} heute={board.heute}
          kalender={board.kalender} onAbbrechen={() => setVerschieben(null)}
          onAblage={(z) => { const id = verschieben.id; setVerschieben(null); void ablegen(id, z); }} />
      )}
      {spaltenDialog !== null && board !== null && (
        // Die Spalte aus dem frischen Stand (Version und Lage nach einem Neuladen); der
        // Dialog selbst bleibt beim Neuladen stehen (Hausregel), seine Eingaben auch.
        <SpaltenDialog client={p.client} spalten={board.spalten}
          spalte={spaltenDialog.spalte === null ? null
            : board.spalten.find((s) => s.id === spaltenDialog.spalte!.id) ?? spaltenDialog.spalte}
          onAbbrechen={() => setSpaltenDialog(null)} onNeuLaden={laden}
          onFertig={() => { setSpaltenDialog(null); void laden(); }} />
      )}
    </section>
  );
}
